/**
 * Générateur — Attendu 965 — « Connaitre les propriétés caractéristiques des
 * côtés opposés et des diagonales. » (libellé officiel, BO)
 *
 * Niveau : 5ème | Domaine : Espace et géométrie / Parallélogrammes
 * Mise en conformité avec la spécification `Attendu-5e/specifications-tanoed/5e/965.json`
 * (campagne 5e, 2026-09-21). La spécification est la seule référence.
 *
 * ── Plan de cartes (version finale) ──────────────────────────────────────────
 *
 *  Objectif entièrement LANGAGIER et LOGIQUE : aucun instrument, aucune
 *  construction, aucun calcul, aucune tolérance de validation
 *  (`objets_geometriques` 6). Deux familles de propriétés seulement, celles que
 *  le programme cite : les côtés opposés et les diagonales.
 *
 *  1. direct     — niveau 1, côtés opposés : compléter « si ABCD est un
 *                  parallélogramme, alors… ». DEUX réponses sont justes, et le
 *                  mode de réponse l'autorise (vigilance 2).
 *  2. demidiag   — niveau 1, diagonales : la propriété qui fait du centre le
 *                  milieu commun des deux diagonales.
 *  3. reciproque — niveau 2 : quelle condition suffit pour conclure qu'un
 *                  quadrilatère NON CROISÉ est un parallélogramme.
 *  4. melange    — niveau 3 : les deux sens dans la même carte ; il faut
 *                  d'abord lire dans quel sens la phrase est écrite.
 *  5. refuter    — niveau 4 : réfuter une formulation insuffisante en
 *                  désignant la FIGURE qui la met en défaut (contre-exemples
 *                  dessinés, `objets_geometriques` 3).
 *  6. statut     — niveau 5 : classer les énoncés en définition, propriété,
 *                  propriété caractéristique, et dire pourquoi.
 *
 * ── Spécification ────────────────────────────────────────────────────────────
 *
 *  Bornes appliquées :
 *   - familles de propriétés : côtés opposés et diagonales, jamais les angles
 *     (exclusion 1, `ecarts_assumes` 1) ;
 *   - énoncés du sens direct : les 3 de la spec, aucun autre ;
 *   - énoncés du sens réciproque : les 4 de la spec, aucun autre ;
 *   - propositions par question : 5 (dans [4 ; 5]), nombre CONSTANT par format ;
 *   - bonnes réponses par question : 2 sur les cartes `direct` et `melange` en
 *     régime direct-côtés, 1 partout ailleurs (dans [1 ; 2]) ;
 *   - hypothèse « quadrilatère non croisé » : écrite dans CHAQUE énoncé
 *     réciproque, jamais renvoyée à une introduction (vigilance 3) ;
 *   - aucun énoncé reposé deux fois dans une même question.
 *  Exclusions : aucun distracteur absurde — chaque proposition fausse est une
 *   formulation voisine dont l'erreur est nommée par son code ; aucune
 *   proposition ne complète grammaticalement autre chose que la phrase
 *   commencée ; aucun énoncé ne mêle une propriété des côtés et une propriété
 *   des diagonales dans la même phrase.
 *
 *  Erreurs visées → mots-clés (codes des distracteurs, `_meta.params`) :
 *   1. traiter une propriété et sa réciproque comme une même phrase
 *      → `sens_confondu` (melange, statut)
 *   2. croire qu'un seul parallélisme suffit (trapèze)
 *      → `un_seul_parallelisme` (reciproque, melange, refuter)
 *   3. croire que deux côtés opposés de même longueur suffisent
 *      → `deux_cotes_egaux_suffisent` (reciproque, melange, refuter)
 *   4. confondre côtés opposés et côtés consécutifs
 *      → `cotes_consecutifs` (direct, reciproque, melange, refuter)
 *   5. confondre le milieu d'un côté et le milieu d'une diagonale
 *      → `milieu_de_cote` (demidiag)
 *   6. attribuer au parallélogramme une propriété du rectangle ou du losange
 *      → `propriete_du_rectangle_ou_losange` (direct, demidiag, melange, statut)
 *   7. ne cocher qu'une réponse quand deux sont justes
 *      → `une_seule_reponse` (`erreurVisee` de direct et de melange en régime
 *        direct-côtés, qui ont deux bonnes réponses)
 *   8. tenir une propriété pour caractéristique sans vérifier la réciproque
 *      → `reciproque_non_verifiee` (statut)
 *
 *  Graduation → cartes : 1 direct et demidiag · 2 reciproque · 3 melange ·
 *   4 refuter · 5 statut. Réglage transverse « inclure les propriétés des
 *   angles » : non implémenté, elles sont exclues par défaut (voir Non tranché).
 *
 *  Non tranché (appliqué tel qu'écrit, signalé dans le rapport) :
 *   - `liste_des_enonces_du_sens_reciproque` retient « deux côtés opposés à la
 *     fois parallèles et de même longueur », qui repose sur DEUX faits, alors
 *     que `ecarts_assumes` 5 dit limiter chaque énoncé à un seul fait. La liste
 *     fermée l'emporte : l'énoncé est servi, et il est le seul à deux faits.
 *   - Le retour « c'est vrai, mais cela ne complète pas cette phrase »
 *     (vigilance 4) relève du rendu de la correction, pas du générateur : les
 *     codes de distracteurs le préparent sans pouvoir l'afficher.
 *   - `nombre_de_questions_par_serie` (3 à 7) relève de l'application.
 */

import {
    randInt, randomChoice, shuffle, pickLettresSains,
    makeQcm, makeClasser,
} from '../../../utils';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 965;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });
const meta = (payload, key, params) => {
    payload._meta.generatorKey = key;
    payload._meta.params = params;
    return payload;
};

const L_DIRECT = 'Les côtés opposés d\'un parallélogramme';
const L_DEMIDIAG = 'La demi-diagonale';
const L_RECIPROQUE = 'Ce qui suffit pour conclure';
const L_MELANGE = 'Dans quel sens la phrase est-elle écrite ?';
const L_REFUTER = 'Réfuter par un contre-exemple';
const L_STATUT = 'Définition, propriété, propriété caractéristique';

const NB_PROPOSITIONS = 5;   // `nombre_de_propositions_par_question` ∈ [4 ; 5]
const POOL_LETTRES = 'ABCDEFGHJKLMNPRSTUVWXYZ'.split('');
const pickLetters = (n) => pickLettresSains(POOL_LETTRES, n);

const E_SENS = 'sens_confondu';
const E_UN_PARALLELISME = 'un_seul_parallelisme';
const E_DEUX_COTES = 'deux_cotes_egaux_suffisent';
const E_CONSECUTIFS = 'cotes_consecutifs';
const E_MILIEU_COTE = 'milieu_de_cote';
const E_RECT_LOSANGE = 'propriete_du_rectangle_ou_losange';
const E_UNE_REPONSE = 'une_seule_reponse';
const E_RECIPROQUE = 'reciproque_non_verifiee';

/** Le quadrilatère nommé, et l'hypothèse « non croisé » toujours visible. */
const nomQuad = (n) => n.join('');

// ═══════════════════════════════════════════════════════════
// LES SEPT ÉNONCÉS DE LA SPÉCIFICATION
// ═══════════════════════════════════════════════════════════

/**
 * Sens direct : « si ABCD est un parallélogramme, alors … »
 * Les trois énoncés de `liste_des_enonces_du_sens_direct`, et rien d'autre.
 */
const DIRECTS = [
    { id: 'D1', famille: 'cotes', texte: 'ses côtés opposés sont parallèles deux à deux' },
    { id: 'D2', famille: 'cotes', texte: 'ses côtés opposés sont de même longueur deux à deux' },
    { id: 'D3', famille: 'diagonales', texte: 'ses diagonales se coupent en leur milieu' },
];

/**
 * Sens réciproque : « si ABCD est un quadrilatère NON CROISÉ dont …, alors
 * ABCD est un parallélogramme. » Les quatre énoncés de
 * `liste_des_enonces_du_sens_reciproque`, et rien d'autre.
 */
const RECIPROQUES = [
    { id: 'R1', famille: 'cotes', texte: 'les côtés opposés sont parallèles deux à deux' },
    { id: 'R2', famille: 'cotes', texte: 'les côtés opposés sont de même longueur deux à deux' },
    { id: 'R3', famille: 'cotes', texte: 'deux côtés opposés sont à la fois parallèles et de même longueur' },
    { id: 'R4', famille: 'diagonales', texte: 'les diagonales se coupent en leur milieu' },
];

/** Compléments FAUX du sens direct, chacun nommant l'erreur qu'il matérialise. */
const FAUX_DIRECTS = [
    { texte: 'ses côtés consécutifs sont de même longueur deux à deux', code: E_CONSECUTIFS },
    { texte: 'ses quatre côtés sont de même longueur', code: E_CONSECUTIFS },
    { texte: 'ses diagonales sont de même longueur', code: E_RECT_LOSANGE },
    { texte: 'ses diagonales sont perpendiculaires', code: E_RECT_LOSANGE },
    { texte: 'ses diagonales sont de même longueur et perpendiculaires', code: E_RECT_LOSANGE },
];

/** Conditions INSUFFISANTES ou fausses pour conclure au parallélogramme. */
const FAUX_RECIPROQUES = [
    { texte: 'deux côtés opposés sont parallèles', code: E_UN_PARALLELISME },
    { texte: 'deux côtés opposés sont de même longueur', code: E_DEUX_COTES },
    { texte: 'les côtés consécutifs sont de même longueur deux à deux', code: E_CONSECUTIFS },
    { texte: 'les diagonales se coupent', code: E_RECT_LOSANGE },
    { texte: 'les diagonales sont de même longueur', code: E_RECT_LOSANGE },
];

// ═══════════════════════════════════════════════════════════
// Carte 1 — direct (niveau 1, côtés opposés) : deux réponses justes
// ═══════════════════════════════════════════════════════════

const genDirectQcm = () => {
    const n = pickLetters(4);
    const bons = DIRECTS.filter((d) => d.famille === 'cotes');   // D1 et D2 : deux réponses justes
    const faux = shuffle(FAUX_DIRECTS).slice(0, NB_PROPOSITIONS - bons.length);
    const options = [
        ...bons.map((d) => ({ choix: d.texte, code: null, id: d.id })),
        ...faux.map((f) => ({ choix: f.texte, code: f.code })),
    ];
    const texte = `<p>Complète la phrase. <b>Si ${nomQuad(n)} est un parallélogramme, alors…</b></p>`
        + '<p><em>Attention : plusieurs réponses peuvent être justes, coche-les toutes.</em></p>';
    const payload = makeQcm(options.map((o) => o.choix), bons.map((d) => d.texte), texte, opts(L_DIRECT), true);
    return meta(payload, 'direct_qcm_multiple', {
        niveau: 1,
        famille: 'cotes opposes',
        sens: 'direct',
        nombre_de_propositions_par_question: NB_PROPOSITIONS,
        nombre_de_bonnes_reponses_par_question: bons.length,
        enonces_servis: bons.map((d) => d.id),
        distracteurs: options.filter((o) => o.code),
        erreurVisee: E_UNE_REPONSE,
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — demidiag (niveau 1, diagonales)
// ═══════════════════════════════════════════════════════════

const genDemidiagQcm = () => {
    const n = pickLetters(4);
    const nO = 'O';
    const options = [
        { choix: 'Les diagonales d\'un parallélogramme se coupent en leur milieu.', code: null },
        { choix: 'Les diagonales d\'un parallélogramme sont de même longueur.', code: E_RECT_LOSANGE },
        { choix: 'Les diagonales d\'un parallélogramme sont perpendiculaires.', code: E_RECT_LOSANGE },
        { choix: `Le point ${nO} est le milieu d'un côté du parallélogramme.`, code: E_MILIEU_COTE },
        { choix: 'Les côtés opposés d\'un parallélogramme sont de même longueur.', code: E_CONSECUTIFS },
    ];
    const texte = `<p>${nomQuad(n)} est un parallélogramme et ${nO} est le point d'intersection de `
        + `ses diagonales. Quelle propriété permet d'affirmer que ${nO} est le <b>milieu</b> de la `
        + `diagonale [${n[0]}${n[2]}] ?</p>`;
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_DEMIDIAG));
    return meta(payload, 'demidiag_qcm_unique', {
        niveau: 1,
        famille: 'diagonales',
        sens: 'direct',
        nombre_de_propositions_par_question: NB_PROPOSITIONS,
        nombre_de_bonnes_reponses_par_question: 1,
        enonces_servis: ['D3'],
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — reciproque (niveau 2)
// ═══════════════════════════════════════════════════════════

const genReciproqueQcm = () => {
    const n = pickLetters(4);
    const bon = randomChoice(RECIPROQUES);
    const faux = shuffle(FAUX_RECIPROQUES).slice(0, NB_PROPOSITIONS - 1);
    const options = [
        { choix: bon.texte, code: null },
        ...faux.map((f) => ({ choix: f.texte, code: f.code })),
    ];
    const texte = `<p>${nomQuad(n)} est un quadrilatère <b>non croisé</b>. Quelle condition permet `
        + `d'affirmer à coup sûr que ${nomQuad(n)} est un parallélogramme ?</p>`
        + `<p><em>L'hypothèse « non croisé » fait partie de l'énoncé : sans elle, la conclusion `
        + 'tomberait en défaut.</em></p>';
    const payload = makeQcm(options.map((o) => o.choix), bon.texte, texte, opts(L_RECIPROQUE));
    return meta(payload, 'reciproque_qcm_unique', {
        niveau: 2,
        famille: bon.famille === 'diagonales' ? 'diagonales' : 'cotes opposes',
        sens: 'reciproque',
        hypothese_de_quadrilatere_non_croise: true,
        nombre_de_propositions_par_question: NB_PROPOSITIONS,
        nombre_de_bonnes_reponses_par_question: 1,
        enonces_servis: [bon.id],
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — melange (niveau 3) : les deux sens dans la même carte
// ═══════════════════════════════════════════════════════════

const genMelangeQcm = () => {
    const n = pickLetters(4);
    const sens = randomChoice(['direct_cotes', 'direct_diagonales', 'reciproque']);
    if (sens === 'reciproque') {
        const bon = randomChoice(RECIPROQUES);
        const faux = shuffle([...FAUX_RECIPROQUES]).slice(0, NB_PROPOSITIONS - 1);
        const options = [
            { choix: bon.texte, code: null },
            ...faux.map((f) => ({ choix: f.texte, code: f.code })),
        ];
        const texte = `<p>Lis bien dans quel sens la phrase est écrite, puis complète-la.</p>`
            + `<p><b>Si ${nomQuad(n)} est un quadrilatère non croisé dont… , alors ${nomQuad(n)} `
            + 'est un parallélogramme.</b></p>'
            + '<p><em>Une seule réponse est juste ici.</em></p>';
        const payload = makeQcm(options.map((o) => o.choix), bon.texte, texte, opts(L_MELANGE));
        return meta(payload, 'melange_qcm_multiple', {
            niveau: 3,
            sens: 'reciproque',
            famille: bon.famille === 'diagonales' ? 'diagonales' : 'cotes opposes',
            hypothese_de_quadrilatere_non_croise: true,
            nombre_de_propositions_par_question: NB_PROPOSITIONS,
            nombre_de_bonnes_reponses_par_question: 1,
            enonces_servis: [bon.id],
            distracteurs: options.slice(1),
            erreursVisees: [E_SENS],
        });
    }
    const bons = sens === 'direct_cotes'
        ? DIRECTS.filter((d) => d.famille === 'cotes')
        : DIRECTS.filter((d) => d.famille === 'diagonales');
    const faux = shuffle(FAUX_DIRECTS).slice(0, NB_PROPOSITIONS - bons.length);
    const options = [
        ...bons.map((d) => ({ choix: d.texte, code: null })),
        ...faux.map((f) => ({ choix: f.texte, code: f.code })),
    ];
    const texte = `<p>Lis bien dans quel sens la phrase est écrite, puis complète-la.</p>`
        + `<p><b>Si ${nomQuad(n)} est un parallélogramme, alors…</b></p>`
        + `<p><em>${bons.length > 1 ? 'Plusieurs réponses sont justes, coche-les toutes.' : 'Une seule réponse est juste ici.'}</em></p>`;
    const payload = makeQcm(options.map((o) => o.choix), bons.map((d) => d.texte), texte, opts(L_MELANGE), true);
    return meta(payload, 'melange_qcm_multiple', {
        niveau: 3,
        sens: 'direct',
        famille: sens === 'direct_cotes' ? 'cotes opposes' : 'diagonales',
        nombre_de_propositions_par_question: NB_PROPOSITIONS,
        nombre_de_bonnes_reponses_par_question: bons.length,
        enonces_servis: bons.map((d) => d.id),
        distracteurs: options.filter((o) => o.code),
        erreursVisees: bons.length > 1 ? [E_SENS, E_UNE_REPONSE] : [E_SENS],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — refuter (niveau 4) : contre-exemples DESSINÉS
// ═══════════════════════════════════════════════════════════

const VB_FIG = { w: 200, h: 160 };

/** Un petit trait perpendiculaire au milieu de [PQ] (codage d'égalité). */
const ticks = (P, Q, n) => {
    const mx = (P.x + Q.x) / 2;
    const my = (P.y + Q.y) / 2;
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        const t = (i - (n - 1) / 2) * 5;
        const cx = mx + ux * t;
        const cy = my + uy * t;
        out += `<line x1="${(cx + uy * 5).toFixed(1)}" y1="${(cy - ux * 5).toFixed(1)}" x2="${(cx - uy * 5).toFixed(1)}" y2="${(cy + ux * 5).toFixed(1)}" stroke="#0f172a" stroke-width="1.6"/>`;
    }
    return out;
};

/** Un chevron au milieu de [PQ], orienté vers Q (codage de parallélisme). */
const chevrons = (P, Q, n) => {
    const mx = (P.x + Q.x) / 2;
    const my = (P.y + Q.y) / 2;
    const dx = Q.x - P.x;
    const dy = Q.y - P.y;
    const L = Math.hypot(dx, dy) || 1;
    const ux = dx / L;
    const uy = dy / L;
    let out = '';
    for (let i = 0; i < n; i++) {
        const t = (i - (n - 1) / 2) * 6;
        const cx = mx + ux * t;
        const cy = my + uy * t;
        out += `<path d="M ${(cx - ux * 4 + uy * 5).toFixed(1)},${(cy - uy * 4 - ux * 5).toFixed(1)} L ${(cx + ux * 4).toFixed(1)},${(cy + uy * 4).toFixed(1)} L ${(cx - ux * 4 - uy * 5).toFixed(1)},${(cy - uy * 4 + ux * 5).toFixed(1)}" fill="none" stroke="#1E3A8A" stroke-width="1.6"/>`;
    }
    return out;
};

const svgFigure = (pts, codage, titre) => {
    const poly = pts.map((p) => `${p.x},${p.y}`).join(' ');
    return `<svg data-v="${titre}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${VB_FIG.w} ${VB_FIG.h}" width="${VB_FIG.w}" height="${VB_FIG.h}">`
        + `<polygon points="${poly}" fill="#f8fafc" stroke="#334155" stroke-width="2"/>${codage}</svg>`;
};

const P = (x, y) => ({ x, y });

/**
 * Les quatre figures du crible, en coordonnées brutes. Une SIMILITUDE (rotation
 * puis mise à l'échelle uniforme) leur est appliquée avant le tracé : elle
 * conserve exactement les parallélismes et les égalités de longueurs, donc la
 * propriété que chaque figure illustre, tout en faisant varier le dessin.
 */
const BRUT_FIGURES = {
    // Trapèze : deux côtés opposés parallèles, de longueurs très différentes.
    trapeze: {
        pts: [P(20, 140), P(175, 140), P(110, 50), P(45, 50)],
        codage: (s) => chevrons(s[0], s[1], 1) + chevrons(s[3], s[2], 1),
    },
    // Cerf-volant : côtés CONSÉCUTIFS égaux deux à deux.
    cerfVolant: {
        pts: [P(98, 20), P(148, 62), P(98, 148), P(48, 62)],
        codage: (s) => ticks(s[0], s[1], 1) + ticks(s[0], s[3], 1)
            + ticks(s[1], s[2], 2) + ticks(s[2], s[3], 2),
    },
    // Quadrilatère quelconque : deux côtés OPPOSÉS de même longueur, non parallèles.
    quelconque: {
        pts: [P(22, 140), P(107, 140), P(124, 81), P(73, 13)],
        codage: (s) => ticks(s[0], s[1], 1) + ticks(s[2], s[3], 1),
    },
    // Parallélogramme : il vérifie les hypothèses ET la conclusion, il ne réfute rien.
    parallelogramme: {
        pts: [P(25, 140), P(120, 140), P(160, 55), P(65, 55)],
        codage: (s) => chevrons(s[0], s[1], 1) + chevrons(s[3], s[2], 1)
            + chevrons(s[1], s[2], 2) + chevrons(s[0], s[3], 2)
            + ticks(s[0], s[1], 1) + ticks(s[3], s[2], 1),
    },
};

/** Rotation de `deg` degrés autour du centre, puis cadrage dans le viewBox. */
const similitude = (pts, deg) => {
    const c = Math.cos((deg * Math.PI) / 180);
    const si = Math.sin((deg * Math.PI) / 180);
    const gx = pts.reduce((a, p) => a + p.x, 0) / pts.length;
    const gy = pts.reduce((a, p) => a + p.y, 0) / pts.length;
    const tourne = pts.map((p) => ({
        x: gx + (p.x - gx) * c - (p.y - gy) * si,
        y: gy + (p.x - gx) * si + (p.y - gy) * c,
    }));
    const xs = tourne.map((p) => p.x);
    const ys = tourne.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    const k = Math.min((VB_FIG.w - 28) / (Math.max(...xs) - minX), (VB_FIG.h - 28) / (Math.max(...ys) - minY));
    const offX = (VB_FIG.w - k * (Math.max(...xs) - minX)) / 2;
    const offY = (VB_FIG.h - k * (Math.max(...ys) - minY)) / 2;
    return tourne.map((p) => ({
        x: Math.round((offX + (p.x - minX) * k) * 10) / 10,
        y: Math.round((offY + (p.y - minY) * k) * 10) / 10,
    }));
};

const figurePosee = (nom, deg) => {
    const pts = similitude(BRUT_FIGURES[nom].pts, deg);
    return { pts, codage: BRUT_FIGURES[nom].codage(pts) };
};

const FORMULATIONS = [
    {
        id: 'un_parallelisme',
        enonce: 'deux de ses côtés opposés sont parallèles',
        contre: 'trapeze',
        code: E_UN_PARALLELISME,
    },
    {
        id: 'consecutifs',
        enonce: 'ses côtés consécutifs sont de même longueur deux à deux',
        contre: 'cerfVolant',
        code: E_CONSECUTIFS,
    },
    {
        id: 'deux_cotes',
        enonce: 'deux de ses côtés opposés sont de même longueur',
        contre: 'quelconque',
        code: E_DEUX_COTES,
    },
];

const CODE_FIGURE = {
    trapeze: E_UN_PARALLELISME,
    cerfVolant: E_CONSECUTIFS,
    quelconque: E_DEUX_COTES,
    parallelogramme: E_RECT_LOSANGE,
};

const choixImage = (nom, deg) => {
    const f = figurePosee(nom, deg);
    return {
        type: 'image',
        config: {
            imageData: `data:image/svg+xml,${encodeURIComponent(svgFigure(f.pts, f.codage, `${nom}_${deg}`))}`,
        },
    };
};

const genRefuterQcm = () => {
    const n = pickLetters(4);
    const f = randomChoice(FORMULATIONS);
    const noms = ['trapeze', 'cerfVolant', 'quelconque', 'parallelogramme'];
    const options = noms.map((nom) => ({
        nom,
        choix: choixImage(nom, randInt(-6, 6) * 5),
        code: nom === f.contre ? null : CODE_FIGURE[nom],
    }));
    const correct = options.find((o) => o.nom === f.contre).choix;
    const texte = `<p>Un élève affirme : « si le quadrilatère non croisé ${nomQuad(n)} est tel que `
        + `${f.enonce}, alors ${nomQuad(n)} est un parallélogramme ». Cette affirmation est `
        + '<b>fausse</b>.</p>'
        + '<p>Quelle figure la met en défaut ? (Elle vérifie ce qui est supposé, mais elle n\'est '
        + 'pas un parallélogramme.)</p>';
    const payload = makeQcm(options.map((o) => o.choix), correct, texte, opts(L_REFUTER));
    return meta(payload, 'refuter_qcm_unique', {
        niveau: 4,
        formulation_refutee: f.id,
        contre_exemple: f.contre,
        nombre_de_propositions_par_question: 4,
        nombre_de_bonnes_reponses_par_question: 1,
        hypothese_de_quadrilatere_non_croise: true,
        distracteurs: options.filter((o) => o.code).map((o) => ({ choix: o.nom, code: o.code })),
        erreursVisees: [f.code],
    });
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — statut (niveau 5)
// ═══════════════════════════════════════════════════════════

/**
 * Les énoncés à classer, écrits sur un quadrilatère NOMMÉ : l'hypothèse « non
 * croisé » figure dans chaque réciproque (vigilance 3), et la formulation d'une
 * même propriété est identique d'une carte à l'autre (vigilance 5).
 */
const DEFINITION = (q) => `Un parallélogramme est un quadrilatère dont les côtés opposés sont parallèles deux à deux, comme ${q}.`;
const PROPRIETES_SIMPLES = [
    (q) => `Si le quadrilatère ${q} est un parallélogramme, alors deux de ses côtés opposés sont parallèles.`,
    (q) => `Si le quadrilatère ${q} est un parallélogramme, alors ses diagonales se coupent.`,
    (q) => `Si le quadrilatère ${q} est un parallélogramme, alors ses côtés opposés ne se coupent pas.`,
];
const CARACTERISTIQUES = [
    (q) => `Le quadrilatère non croisé ${q} est un parallélogramme si et seulement si ses côtés opposés sont de même longueur deux à deux.`,
    (q) => `Le quadrilatère non croisé ${q} est un parallélogramme si et seulement si ses diagonales se coupent en leur milieu.`,
    (q) => `Le quadrilatère non croisé ${q} est un parallélogramme si et seulement si deux de ses côtés opposés sont à la fois parallèles et de même longueur.`,
];

const genStatutClasser = () => {
    const n = pickLetters(4);
    const q = nomQuad(n);
    const propres = shuffle(PROPRIETES_SIMPLES).slice(0, 2).map((g) => g(q));
    const carac = shuffle(CARACTERISTIQUES).slice(0, 2).map((g) => g(q));
    const categories = [
        { name: 'Définition', items: [DEFINITION(nomQuad(n))] },
        { name: 'Propriété', items: propres },
        { name: 'Propriété caractéristique', items: carac },
    ];
    const texte = '<p>Range chaque énoncé selon son <b>statut</b>. Une propriété est dite '
        + '<b>caractéristique</b> quand elle et sa réciproque sont toutes les deux vraies — on peut '
        + 'alors l\'écrire avec « si et seulement si ».</p>'
        + '<p><em>En classe de 5e, ces énoncés sont tous admis : on ne les démontre pas.</em></p>';
    const payload = makeClasser(categories, texte, opts(L_STATUT));
    return meta(payload, 'statut_classer', {
        niveau: 5,
        statut_de_demonstration_annonce: 'admise',
        statuts_proposes: ['définition', 'propriété', 'propriété caractéristique'],
        nombre_de_bonnes_reponses_par_question: 1,
        erreursVisees: [E_RECIPROQUE, E_SENS],
    });
};

const PROPRIETES_CARACTERISTIQUES_COURTES = [
    { texte: 'les côtés opposés sont de même longueur deux à deux', famille: 'cotes opposes' },
    { texte: 'les diagonales se coupent en leur milieu', famille: 'diagonales' },
    { texte: 'deux côtés opposés sont à la fois parallèles et de même longueur', famille: 'cotes opposes' },
];

const genStatutQcm = () => {
    const n = pickLetters(4);
    const q = nomQuad(n);
    const p = randomChoice(PROPRIETES_CARACTERISTIQUES_COURTES);
    const options = [
        {
            choix: `Parce que la propriété et sa réciproque sont toutes les deux vraies : ${q} non croisé est un parallélogramme si et seulement si ${p.texte}.`,
            code: null,
        },
        { choix: `Parce que tout parallélogramme, comme ${q}, possède cette propriété.`, code: E_RECIPROQUE },
        { choix: `Parce que la propriété et sa réciproque disent la même chose de ${q}.`, code: E_SENS },
        { choix: `Parce que c'est la propriété qui distingue le parallélogramme ${q} d'un rectangle.`, code: E_RECT_LOSANGE },
        { choix: `Parce qu'elle se démontre pour ${q}, alors que les autres propriétés sont admises.`, code: E_RECIPROQUE },
    ];
    const texte = `<p>Dans le quadrilatère non croisé ${q}, on dit que « ${p.texte} » est une propriété `
        + '<b>caractéristique</b> du parallélogramme. Pourquoi ?</p>';
    const payload = makeQcm(options.map((o) => o.choix), options[0].choix, texte, opts(L_STATUT));
    return meta(payload, 'statut_qcm_unique', {
        niveau: 5,
        famille: p.famille,
        statut_de_demonstration_annonce: 'admise',
        nombre_de_propositions_par_question: NB_PROPOSITIONS,
        nombre_de_bonnes_reponses_par_question: 1,
        distracteurs: options.slice(1),
    });
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    direct_qcm_multiple: genDirectQcm,
    demidiag_qcm_unique: genDemidiagQcm,
    reciproque_qcm_unique: genReciproqueQcm,
    melange_qcm_multiple: genMelangeQcm,
    refuter_qcm_unique: genRefuterQcm,
    statut_classer: genStatutClasser,
    statut_qcm_unique: genStatutQcm,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', classer: 'Classer',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'direct', label: L_DIRECT, formatsList: buildFormats('direct') },
    { id: 'demidiag', label: L_DEMIDIAG, formatsList: buildFormats('demidiag') },
    { id: 'reciproque', label: L_RECIPROQUE, formatsList: buildFormats('reciproque') },
    { id: 'melange', label: L_MELANGE, formatsList: buildFormats('melange') },
    { id: 'refuter', label: L_REFUTER, formatsList: buildFormats('refuter') },
    { id: 'statut', label: L_STATUT, formatsList: buildFormats('statut') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    DIRECTS, RECIPROQUES, FAUX_DIRECTS, FAUX_RECIPROQUES, BRUT_FIGURES, figurePosee, FORMULATIONS,
    DEFINITION, PROPRIETES_SIMPLES, CARACTERISTIQUES,
};
